import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { SrOnly } from 'components/common/styled/common-styled';
import RadioButton from 'components/theme/RadioButton/RadioButton';
import { useGlobalDateLocale } from 'context/DateLocaleContext';
import { formatTimeInAMPM } from 'utils/dateTime';

const RadioWrapper = ({ sessionTimes, value, name, labelId, ...props }) => {
  const { t: translate } = useTranslation();
  const { locale } = useGlobalDateLocale();

  const getSlotsMessage = (supportedCount) => {
    if (supportedCount > 1)
      return translate('slotsLeft', {
        remainSlots: supportedCount,
      });
    return translate('slotLeft', { remainSlots: supportedCount });
  };

  return (
    <RadioGroupWrapper aria-labelledby={labelId}>
      <SrOnly as="legend">{translate('selectSessionTime')}</SrOnly>
      {sessionTimes?.map(
        ({ startTime, isDisabled, supportedParticipantsCount }) => {
          const shouldShowSlots =
            supportedParticipantsCount > 0 &&
            supportedParticipantsCount <= 10 &&
            !isDisabled;
          return (
            <SessionTimeWrapper key={`start-time-${startTime}`}>
              <RadioButton
                asBtn
                name={name}
                disabled={isDisabled}
                value={startTime}
                checked={value === startTime}
                {...props}
              >
                {formatTimeInAMPM(startTime, locale)}
              </RadioButton>

              <ParticipantSlots isLimitExceed={isDisabled}>
                {shouldShowSlots && getSlotsMessage(supportedParticipantsCount)}
              </ParticipantSlots>
            </SessionTimeWrapper>
          );
        },
      )}
    </RadioGroupWrapper>
  );
};

const RadioGroupWrapper = styled.fieldset`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  border: 0;
  padding: 0;
  outline: 0;
`;

const SessionTimeWrapper = styled.span`
  flex: 0 1 calc(33.333% - 6px);
  text-align: center;
  min-height: 50px;
`;

const ParticipantSlots = styled.span`
  font-size: 12px;
  color: var(--way-colors-secondaryTextColor);
  opacity: ${({ isLimitExceed }) => (isLimitExceed ? 0.7 : 1)};
  font-style: italic;
`;

RadioWrapper.propTypes = {
  sessionTimes: PropTypes.array.isRequired,
  value: PropTypes.string,
  name: PropTypes.string,
  labelId: PropTypes.string,
};

RadioWrapper.defaultProps = {
  value: '',
  name: 'sessionTime',
  labelId: undefined,
};

export default RadioWrapper;
